// Diálogo y carrusel reutilizables.
// Componente pdp-*: el HTML llega renderizado desde Liquid y este módulo solo añade interacción.

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};
const lockScroll = (locked) => {
  document.documentElement.style.overflow = locked ? 'hidden' : '';
};

/* ---------- Diálogo (beneficios) ---------- */
class PdpDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.trigger = this.querySelector('[data-open]');
    if (!this.dialog || !this.trigger) return;
    this.trigger.addEventListener('click', () => {
      this.dialog.showModal();
      lockScroll(true);
    });
    this.dialog.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => {
      lockScroll(false);
      this.trigger.focus();
    });
  }
}
define('pdp-dialog', PdpDialog);

/* ---------- Carrusel con desplazamiento nativo ---------- */
class PdpCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.prev?.addEventListener('click', () => this.scrollByPage(-1));
    this.next?.addEventListener('click', () => this.scrollByPage(1));
    this.onScroll = () => requestAnimationFrame(() => this.update());
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    // Measured on resize, not per frame: reading offsetLeft while scrolling forces reflows.
    this.resizeObserver = new ResizeObserver(() => { this.measure(); this.update(); });
    this.resizeObserver.observe(this.track);
    this.items = [...this.track.children];
    this.counter = this.querySelector('[data-carousel-current]');
    this.measure();
    if (this.hasAttribute('data-dots')) this.buildDots();
    this.update();
    // Loop and autoplay live apart: only the carousel that asks for them downloads them.
    if (this.hasAttribute('data-loop') || this.dataset.autoplay) {
      import('./pdp-carousel-loop.js')
        .then(({ enableLoop, enableAutoplay }) => {
          if (this.hasAttribute('data-loop')) enableLoop(this);
          if (this.dataset.autoplay) enableAutoplay(this);
        })
        .catch(() => {});
    }

    // Mouse drag. Loaded apart so that, if it fails, the carousel still works.
    this.track.addEventListener('pointerdown', () => { this.dragStartIndex = this.currentIndex(); });
    import('./pdp-drag.js')
      .then(({ enableDrag }) => enableDrag(this.track, {
        onRelease: (direction) => {
          this.pause?.();
          if (this.stepBy) { this.stepBy(direction); return; }
          const target = direction === 0 ? this.currentIndex() : this.dragStartIndex + direction;
          this.goTo(Math.max(0, Math.min(this.items.length - 1, target)));
        },
      }))
      .catch(() => {});
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    clearInterval(this.timer);
    clearTimeout(this.resume);
  }

  measure() {
    const origin = this.track.offsetLeft;
    this.offsets = this.items.map((item) => item.offsetLeft - origin);
    this.tailOffset = this.loop ? this.loop.offsetLeft - origin : null;
    this.trackWidth = this.track.clientWidth;
    this.scrollSpan = this.track.scrollWidth;
  }

  scrollByPage(direction) {
    if (this.stepBy) { this.stepBy(direction); return; }
    this.track.scrollBy({ left: direction * this.trackWidth * 0.8, behavior: 'smooth' });
  }

  goTo(index, behavior = 'smooth') {
    const left = this.offsets[index];
    if (left !== undefined) this.track.scrollTo({ left, behavior });
  }

  currentIndex(left = this.track.scrollLeft) {
    let closest = 0;
    this.offsets.forEach((offset, index) => {
      if (Math.abs(offset - left) < Math.abs(this.offsets[closest] - left)) closest = index;
    });
    return closest;
  }

  buildDots() {
    if (this.items.length < 2) return;
    this.dots = document.createElement('div');
    this.dots.className = 'pdp-carousel__dots';
    this.items.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'pdp-carousel__dot';
      dot.setAttribute('aria-label', `${index + 1} / ${this.items.length}`);
      // `pause` solo existe con autoavance: sin él, llamarla rompía el salto al punto.
      dot.addEventListener('click', () => { this.pause?.(); this.goTo(index); });
      this.dots.append(dot);
    });
    this.append(this.dots);
  }

  // Reads first, writes after, and only what changed: reading geometry after writing to
  // the DOM forces a reflow, which is the very cost this component avoids.
  update() {
    const left = this.track.scrollLeft;
    const atEnd = left + this.trackWidth >= this.scrollSpan - 4;
    const active = atEnd && !this.loop ? this.items.length - 1 : this.currentIndex(left);
    this.classList.toggle('is-scrollable', this.scrollSpan > this.trackWidth + 4);
    // At the ends the arrow that leads nowhere is hidden, not just greyed out. The start is
    // measured from the first item, because the track padding shifts it a few pixels.
    // With a loop there is no end, so no arrow hides.
    const hidePrev = !this.loop && left <= Math.max(0, this.offsets[0] ?? 0) + 4;
    const hideNext = !this.loop && atEnd;
    if (this.prev && this.prev.hidden !== hidePrev) this.prev.hidden = hidePrev;
    if (this.next && this.next.hidden !== hideNext) this.next.hidden = hideNext;
    if (active === this.active) return;
    this.active = active;
    if (this.dots) {
      [...this.dots.children].forEach((dot, index) => dot.toggleAttribute('data-active', index === active));
    }
    if (this.counter) this.counter.textContent = String(active + 1).padStart(2, '0');
  }
}
define('pdp-carousel', PdpCarousel);

/* ---------- Entrada de las tarjetas de beneficios ---------- */
// El icono se dibuja a velocidad constante y un trazo espera al anterior: primero el contorno,
// después el detalle. Las tres constantes son el único sitio donde se ajusta el ritmo.
const STROKE_SPEED = 0.008; // segundos por unidad de longitud del trazo
const STROKE_GAP = 0.08; // pausa entre un trazo y el siguiente, en segundos
const STROKE_MAX = 0.9; // tope del icono completo: los de muchos trazos se dibujan más rápido
const CARD_GAP = 60; // milisegundos entre tarjetas que entran a la vez

class PdpReveal extends HTMLElement {
  connectedCallback() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.items = [...this.querySelectorAll('.pdp-benefit')];
    if (!this.items.length) return;
    for (const item of this.items) {
      this.#timeStrokes(item);
      // Lo que ya está en pantalla al cargar no se anima: si no, parpadearía.
      const box = item.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) item.classList.add('is-in');
    }
    this.classList.add('is-armed');
    // La zona observada se extiende muy por encima de la pantalla: así una tarjeta que ya pasó
    // sigue contando como vista y, si un salto (volver arriba, un ancla) la deja otra vez bajo
    // el borde, el observador avisa y se rearma. Con la pantalla sola, ese salto no se detecta.
    this.observer = new IntersectionObserver((entries) => this.#onIntersect(entries), {
      rootMargin: '100000px 0px 0px 0px',
      threshold: [0, 0.3],
    });
    for (const item of this.items) this.observer.observe(item);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  #onIntersect(entries) {
    let batch = 0;
    for (const entry of entries) {
      const item = entry.target;
      if (entry.intersectionRatio >= 0.3) {
        if (item.classList.contains('is-in')) continue;
        item.style.setProperty('--pdp-reveal-delay', `${batch * CARD_GAP}ms`);
        item.classList.add('is-in');
        batch += 1;
        continue;
      }
      if (entry.isIntersecting) continue;
      // Se rearma solo si la tarjeta volvió a quedar por debajo del borde inferior. Salir por
      // arriba no la apaga: lo que ya se leyó no puede desaparecer al subir la página.
      const rootBottom = entry.rootBounds?.bottom;
      if (rootBottom === undefined || entry.boundingClientRect.top < rootBottom) continue;
      item.classList.remove('is-in');
      item.style.removeProperty('--pdp-reveal-delay');
    }
  }

  // Mide cada trazo del icono y deja su duración y su turno en variables CSS.
  #timeStrokes(item) {
    const strokes = [];
    for (const stroke of item.querySelectorAll('.pdp-benefit__icon svg :is(path, circle, ellipse, line, polyline, polygon, rect)')) {
      const length = stroke.getTotalLength?.();
      if (length) strokes.push({ stroke, length });
    }
    if (!strokes.length) return;
    // Un icono de cinco trazos tardaría más de un segundo a velocidad fija: cuando pasa del
    // tope, todos sus trazos se dibujan proporcionalmente más rápido.
    const total = strokes.reduce((sum, item) => sum + item.length, 0);
    const pauses = STROKE_GAP * (strokes.length - 1);
    const speed = Math.min(STROKE_SPEED, (STROKE_MAX - pauses) / total);
    let start = 0;
    for (const { stroke, length } of strokes) {
      stroke.style.setProperty('--pdp-stroke-length', length.toFixed(1));
      stroke.style.setProperty('--pdp-stroke-time', `${(length * speed).toFixed(3)}s`);
      stroke.style.setProperty('--pdp-stroke-delay', `${start.toFixed(3)}s`);
      start += length * speed + STROKE_GAP;
    }
  }
}
define('pdp-reveal', PdpReveal);
